import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Analytics, IAnalytics, Locations, backButtonClicked, backToEventRegistrationClicked } from '../../core/services/analytics/module';
import { Auth, IAuth } from '../../core/services/auth/auth';
import { Events, IEvents } from '../../core/services/events/events';
import { QueryParams, Routes } from '../../core/enums/module';
import { safelyExecute } from '../../utilities/safelyExecute';
import { getRouteBackLink } from '../../utilities/getRouteBackLink';
import { getQueryParameters } from '../../utilities/getQueryParameter';
import { shouldUseCabelasBranding } from '../../utilities/shouldUseCabelasBranding';
import { Icons } from '../../icons';
import styles from './backButton.module.scss';

export enum EventRegistrationSites {
	Cabelas = 'https://events.cabelas-shield.com',
	USCCA ='https://eventsregistration.usconcealedcarry.com'
}

type Props = {
	additionalClass?: string;
	getPathnameFunc?: () => string;
	LinkComponent?: typeof Link;
	analytics?: IAnalytics;
	auth?: IAuth;
	eventsService?: IEvents;
	getQueryParametersFunc?: typeof getQueryParameters;
	isCabelasFunc?: () => boolean;
};

// eslint-disable-next-line complexity
export function BackButton({
	additionalClass,
	getPathnameFunc = () => globalThis.location.pathname,
	LinkComponent = Link,
	analytics = Analytics.Instance(),
	auth = Auth.Instance(),
	eventsService = Events.Instance(),
	getQueryParametersFunc = getQueryParameters,
	isCabelasFunc = shouldUseCabelasBranding
}: Props) {
	const [backLinkLocation, setBackLinkLocation] = useState<string>('');
	const [resumeRegistrationUrl, setResumeRegistrationUrl] = useState<string>('');
	const [defaultRegistrationUrl, setDefaultRegistrationUrl] = useState<string>();
	const onEmailRoute = (getPathnameFunc() as Routes) === Routes.Email;
	const onWelcomeRoute = (getPathnameFunc() as Routes) === Routes.SignupWelcome;
	const urlParams = getQueryParametersFunc();
	const eventId = urlParams.get(QueryParams.RegistrationEventId);

	useEffect(() => {
		setBackLinkLocation(getRouteBackLink(getPathnameFunc() as Routes) || '');
		safelyExecute(() => {
			if (!resumeRegistrationUrl && !onWelcomeRoute) {
				if (eventId && !defaultRegistrationUrl) {
					safelyExecute(async () => {
						const eventDataResponse = await eventsService.getEventData(eventId);
						if (eventDataResponse.wasSuccessful && !defaultRegistrationUrl) {
							const defaultRegistrationSite = isCabelasFunc()
								? EventRegistrationSites.Cabelas : EventRegistrationSites.USCCA;
							setDefaultRegistrationUrl(`${defaultRegistrationSite}/${eventId}`);
						}
					});
				}
				if (eventId && auth.accessToken) {
					safelyExecute(async () => {
						const eventRegistrationResumeResponse = await eventsService.getResumeUrl(eventId, auth.accessToken);
						if (eventRegistrationResumeResponse.wasSuccessful && !resumeRegistrationUrl) {
							setResumeRegistrationUrl(eventRegistrationResumeResponse.value?.data?.attributes?.url || '');
						}
					});
				}
			}
		});
	});

	const eventRegistrationUrl = resumeRegistrationUrl || defaultRegistrationUrl;
	const showEventRegistrationLink = !!eventRegistrationUrl && !onWelcomeRoute;

	return (<>
		{showEventRegistrationLink && <>
			<LinkComponent href={eventRegistrationUrl} onClick={() => {
				analytics.TrackInteraction(backToEventRegistrationClicked(Locations.Content));
			}} className={`${styles.backLinkHeader} ${styles.eventRegistrationLink} ${onEmailRoute ? styles.lightLink : ''} ${additionalClass ? additionalClass : ''}`}>
				{Icons.LeftArrow}Back to Event Registration
			</LinkComponent>
		</>}
		{!!backLinkLocation && <>
			<LinkComponent href={`${backLinkLocation}${location.search}`} onClick={() => {
				analytics.TrackInteraction(backButtonClicked(Locations.Content));
			}} className={`${styles.backLinkHeader} ${additionalClass ? additionalClass : ''}`}>
				{Icons.LeftArrow}{showEventRegistrationLink ? 'Previous Step' : 'Back'}
			</LinkComponent>
		</>}
	</>
	);
}
